import {
  ApiCoverageBook,
  ApiCoverageSide,
  IApiCoverageRule,
  IApiCoverageRuleValidationError,
  IApiCoverageRuleWithError,
  IApiCoverageRuleWithWarning,
  IApiCoverageRuleWrite,
} from '@cbridge-packages/api-coverage';
import { IApiErrorParams, IApiErrorWithRejectedRecords, IApiRejectedRecord } from '@cbridge-packages/api-base';
import { interpolateErrorParams } from '@cbridge-utils/formatters';
import { Side } from '@cbridge-packages/api';
import {
  IRuleUselessMessageItem,
  IRuleValidationResult,
  IRuleWarnings,
  RuleErrorCode,
  RuleErrorKey,
  RuleValidationError,
} from '@cbridge-packages/rule';
import { DELETED_COVERAGE_ACCOUNT_ID } from '@cbridge-packages/coverage-account';
import { isErrorWithRejectedRecords } from '@cbridge-packages/notification';
import { Book, ICoverageRule } from './coverageRule.interface';

export interface ICoverageRuleRejection {
  // rules named by the backend, as validation results addressed by index in the sent array
  result: IRuleValidationResult[];
  // what is left to notify about; undefined when every rejected record named a rule
  restError?: IApiErrorParams | IApiErrorWithRejectedRecords;
  // the error without its rejected records
  baseError: IApiErrorParams;
}

const fromApiErrors = (errors?: IApiCoverageRuleValidationError[]): RuleValidationError[] | undefined =>
  errors?.map((error) => ({
    code: RuleErrorCode.NotFound,
    key: error.key as RuleErrorKey,
    ref: error.ref,
  }));

// CP names the offending rule in `name` as its 0-based index in the sent array.
const getRejectedRuleIndex = (record: IApiRejectedRecord): number | undefined =>
  (record.name !== undefined && /^\d+$/.test(record.name) ? Number(record.name) : record.index);

export namespace CoverageRuleTranslator {
  // The read shape resolves conditions to {id, name} objects, but the result carries only a
  // string account reference (no accountId) — it is re-resolved via the report's account
  // list (keyed by name AND externalId). A resolved target is normalized to the account's
  // NAME label (matching the editor options); unresolved renders as notFound (-1).
  export function fromApi(
    rule: IApiCoverageRule,
    accountByKey: Map<string, { accountId: number; name: string }>,
  ): ICoverageRule {
    const resolvedAccount = rule.result && accountByKey.get(rule.result.account);
    return {
      book: rule.book as Book | undefined,
      side: rule.side as Side | undefined,
      lp: rule.lp?.map((o) => ({ value: o.id, label: o.name })),
      server: rule.server && { value: rule.server.id, label: rule.server.name ?? '' },
      coreSymbols: rule.coreSymbols?.map((o) => ({ value: o.id, label: o.name ?? '' })),
      symbolGroup: rule.symbolGroup,
      clientSymbols: rule.clientSymbols?.map((name) => ({ value: name, label: name })),
      accounts: rule.accounts,
      accountRegexp: rule.accountRegexp,
      accountGroups: rule.accountGroups,
      accountGroupRegexp: rule.accountGroupRegexp,
      targetAccount: rule.result && {
        value: resolvedAccount?.accountId ?? DELETED_COVERAGE_ACCOUNT_ID,
        label: resolvedAccount?.name ?? rule.result.account,
      },
      comment: rule.comment,
      errors: fromApiErrors(rule.errors),
      warnings: rule.warnings as IRuleWarnings | undefined,
    };
  }

  // The write shape is flat ids; accountId is REQUIRED by the contract, so a rule without a
  // target account is sent as -1 (missing entity) and rejected by validation.
  export function toApi(rule: ICoverageRule): IApiCoverageRuleWrite {
    return {
      book: (rule.book as ApiCoverageBook) || undefined,
      side: (rule.side as ApiCoverageSide) || undefined,
      lpId: rule.lp?.map((o) => o.value),
      serverId: rule.server?.value,
      symbolGroup: rule.symbolGroup || undefined,
      // minItems: 1 in the contract — an empty selection must omit the field entirely
      coreSymbols: rule.coreSymbols?.length ? rule.coreSymbols.map((o) => o.value) : undefined,
      clientSymbols: rule.clientSymbols?.map((o) => o.value),
      accounts: rule.accounts,
      accountRegexp: rule.accountRegexp,
      accountGroups: rule.accountGroups,
      accountGroupRegexp: rule.accountGroupRegexp,
      accountId: rule.targetAccount?.value ?? DELETED_COVERAGE_ACCOUNT_ID,
      comment: rule.comment || undefined,
    };
  }

  export function fromApiValidation(
    validation: IApiCoverageRuleWithError | IApiCoverageRuleWithWarning,
  ): IRuleValidationResult {
    return {
      index: validation.index,
      errors: 'errors' in validation ? fromApiErrors(validation.errors) : undefined,
      warnings: 'warnings' in validation ? (validation.warnings as IRuleWarnings) : undefined,
    };
  }

  // A 400 with rejectedRecords (e.g. B-Book combined with LP) rejects the whole request and
  // names the offending rules by index, saying nothing about the rest. Named rules become
  // "useless" (self-conflicting) warnings carrying the backend text verbatim; records that
  // name no rule stay a notification.
  export function fromApiRejection(error: IApiErrorParams): ICoverageRuleRejection {
    if (!isErrorWithRejectedRecords(error)) {
      return { result: [], restError: error, baseError: error };
    }

    const { rejectedRecords, ...baseError } = error;
    const itemsByIndex = new Map<number, IRuleUselessMessageItem[]>();
    const otherRecords: IApiRejectedRecord[] = [];

    rejectedRecords.forEach((record) => {
      const index = getRejectedRuleIndex(record);
      if (index === undefined) {
        otherRecords.push(record);
        return;
      }
      const items = itemsByIndex.get(index) ?? [];
      items.push({ message: interpolateErrorParams(record.errorDescription, record.params) });
      itemsByIndex.set(index, items);
    });

    const result = Array.from(itemsByIndex, ([index, useless]) => ({ index, warnings: { useless } }));

    let restError: ICoverageRuleRejection['restError'] = baseError;
    if (otherRecords.length > 0) {
      restError = { ...baseError, rejectedRecords: otherRecords };
    } else if (result.length > 0) {
      restError = undefined;
    }

    return { result, restError, baseError };
  }
}
